import { useMemo, useCallback } from 'react'
import { baseUrl } from '@fs/zion-config'
import { trackEvent } from '@fs/zion-analytics'
import { splunk } from '@fs/zion-splunk-logger'
import { getImage, getNestedSubelementsByType, useSlsStateContext } from '@fs/zion-sls'
import { useNamesExperienceToggle } from './useNamesExperienceOptIn'
import useSearchFormParam from '../../shared/hooks/useSearchFormParam'

/**
 * @typedef {Object} AttachImageToTreeData
 * @property {string} imageURL - The current page URL for the image being attached
 * @property {string} key - React key for forcing re-renders when image/title changes
 * @property {string} imageTitle - The image title
 * @property {string[]} attachmentUrls - Array of URLs for image and person attachments
 * @property {Function} onAttachImageToTree - Callback function when image is attached to tree
 */

/**
 * Custom hook to provide all data needed for the AttachImageToTree component
 * Includes default analytics tracking and logging, with customizable parameters
 *
 * @param {string} imageTitle - The title of the current image
 * @param {Function} [onAttachImageToTree] - Optional custom callback to override default, (Default provided will track analytics and log to Splunk)
 * @param {string} [linkName='attach-to-tree-completed'] - Analytics link name
 * @param {string} [eventName='collection_image_attach'] - Analytics event name
 * @param {string} [splunkMessage='Attach to Tree completed'] - Splunk log message
 * @returns {AttachImageToTreeData} Props object for AttachImageToTree component
 */
const useAttachImageToTreeData = (
  imageTitle,
  onAttachImageToTree,
  linkName = 'attach-to-tree-completed',
  eventName = 'collection_image_attach',
  splunkMessage = 'Attach to Tree completed'
) => {
  const zionSlsState = useSlsStateContext()
  const currentImage = getImage(zionSlsState.images, zionSlsState.currentImageArk)
  const { isOptedIn, turnOn } = useNamesExperienceToggle()
  const searchForm = useSearchFormParam()

  const attachmentUrls = useMemo(() => {
    const urlList = []
    urlList.push(zionSlsState.currentImageArk)
    const persons = getNestedSubelementsByType(currentImage, 'PERSON')
    persons.forEach((person) => {
      urlList.push(person.id)
    })

    // You can attach people from records on other images on this page, so we've got to load their attachments.
    const personsFromRecordsOnOtherImages =
      currentImage?.floatingElements?.filter(
        (fe) => fe.elementType === 'PERSON' && fe.subElements?.length // Floating people can't be attached
      ) || []
    personsFromRecordsOnOtherImages.forEach((person) => {
      urlList.push(person.id)
    })
    return urlList.map((ark) => `${baseUrl}/ark:/61903/${ark}`)
  }, [zionSlsState.currentImageArk, currentImage])

  const imageURL = window.location?.href
  // Adding the length of the image title to the key to force a re-render when the title changes
  const key = `${zionSlsState?.currentImageArk}_${imageTitle?.length || 0}`

  const defaultOnAttachImageToTree = useCallback(
    (response) => {
      const attachedPersonId = response?.responses?.[0]?.person?.id
      trackEvent({
        link_name: linkName,
        event_name: eventName,
        attachees: response?.responses?.length,
        ...(searchForm && { search_form: searchForm }),
      })
      splunk.log({
        message: splunkMessage,
        imageArk: zionSlsState?.currentImageArk,
        personId: attachedPersonId,
      })
    },
    [linkName, eventName, splunkMessage, zionSlsState?.currentImageArk, searchForm]
  )

  const optInNewFlowFunction = useCallback(() => {
    turnOn()
  }, [turnOn])

  return {
    imageURL,
    key,
    imageTitle,
    attachmentUrls,
    optInNewFlowFunction,
    isOptedInAttachFlow: isOptedIn,
    onAttachImageToTree: onAttachImageToTree || defaultOnAttachImageToTree,
  }
}

export default useAttachImageToTreeData
